/**
 * Session Setup tab panel: declare or revise the situation vector, and see the
 * corroborating evidence the product already holds — as prompts to confirm,
 * never as answers written for you.
 *
 * Saves to the session row (`situation_vector`, migration 715) through
 * useSessionStore.updateSession, so it survives refresh and is visible to
 * every participant. Declined prompts are remembered on the session's
 * metadata so they are not re-asked for that session.
 */

import { useEffect, useMemo, useState } from 'react';
import { Callout } from '../../ds';
import { useSessionStore } from '../../../stores/prioritization/sessionStore';
import {
  EMPTY_SITUATION_SIGNALS,
  deriveCorroborationPrompts,
  fetchSituationSignals,
  type CorroborationPrompt,
  type SituationSignals,
} from '../../../lib/situationCorroboration';
import type { PrioritizationSession } from '../../../types/prioritization/session';
import {
  SITUATION_VECTOR_VERSION,
  isEmptySituationVector,
  situationVectorEquals,
  type SituationVector,
} from '../../../types/prioritization/situationVector';
import { SituationQuestions } from './SituationQuestions';

const DISMISSED_KEY = 'situationPromptsDismissed';

function dismissedFrom(session: PrioritizationSession): string[] {
  const raw = session.metadata?.[DISMISSED_KEY];
  return Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string') : [];
}

export function SituationPanel({ session }: { session: PrioritizationSession }) {
  const updateSession = useSessionStore((s) => s.updateSession);
  const [draft, setDraft] = useState<SituationVector | null>(session.situationVector ?? null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signals, setSignals] = useState<SituationSignals>(EMPTY_SITUATION_SIGNALS);

  // Follow the row when it changes underneath us (another tab, realtime).
  useEffect(() => {
    setDraft(session.situationVector ?? null);
  }, [session.id, session.situationVector]);

  useEffect(() => {
    let cancelled = false;
    if (!session.workspaceId) return;
    void fetchSituationSignals(session.workspaceId).then((s) => {
      if (!cancelled) setSignals(s);
    });
    return () => {
      cancelled = true;
    };
  }, [session.workspaceId]);

  const participantCount = session.participantIds?.length ?? 0;
  const dismissed = useMemo(() => dismissedFrom(session), [session]);
  const prompts = useMemo(
    () => deriveCorroborationPrompts(signals, { participantCount, vector: draft, dismissed }),
    [signals, participantCount, draft, dismissed],
  );

  const dirty = !situationVectorEquals(draft, session.situationVector ?? null);

  const save = async () => {
    setSaving(true);
    setError(null);
    const empty = isEmptySituationVector(draft);
    const result = await updateSession(session.id, {
      situationVector: empty ? null : draft,
      situationVectorVersion: empty ? null : SITUATION_VECTOR_VERSION,
    });
    setSaving(false);
    if (!result.success) setError(result.error ?? 'Could not save the situation.');
  };

  const accept = (prompt: CorroborationPrompt) => {
    setDraft({ ...(draft ?? {}), [prompt.dimension]: prompt.suggestedValue } as SituationVector);
  };

  const decline = async (prompt: CorroborationPrompt) => {
    const result = await updateSession(session.id, {
      metadata: { ...(session.metadata ?? {}), [DISMISSED_KEY]: [...dismissed, prompt.key] },
    });
    if (!result.success) setError(result.error ?? 'Could not record that.');
  };

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6" data-testid="situation-panel">
      <div className="mb-1 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-[var(--text-pri)]">The room</h3>
          <p className="mt-0.5 text-sm text-[var(--text-sec)]">
            Four optional questions about the group this decision is made in. They change which method is
            recommended, and why. Visible to every participant; they describe the process, not the people.
          </p>
        </div>
      </div>

      <div className="mt-4">
        <SituationQuestions value={draft} onChange={setDraft} disabled={saving} />
      </div>

      {prompts.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="situation-prompts">
          {prompts.map((prompt) => (
            <Callout key={prompt.key} tone="slate" label="Evidence">
              <p className="text-sm">{prompt.text}</p>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => accept(prompt)}
                  className="border border-slate-blue-500 bg-slate-blue-50 px-2.5 py-1 text-xs font-medium text-slate-blue-800 hover:bg-slate-blue-100 dark:bg-slate-blue-900/30 dark:text-slate-blue-200"
                >
                  Yes, set it
                </button>
                <button
                  type="button"
                  onClick={() => void decline(prompt)}
                  className="border border-[var(--rule)] px-2.5 py-1 text-xs font-medium text-[var(--text-sec)] hover:border-slate-blue-400"
                >
                  No, leave it
                </button>
              </div>
            </Callout>
          ))}
        </div>
      )}

      {error && (
        <p className="mt-3 text-xs text-[var(--coral)]" role="alert">
          {error}
        </p>
      )}

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={() => void save()}
          disabled={!dirty || saving}
          className="px-4 py-2 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 transition-colors"
        >
          {saving ? 'Saving…' : 'Save the room'}
        </button>
        {dirty && !saving && <span className="text-xs text-[var(--text-ter)]">Unsaved changes</span>}
        {!dirty && session.situationVector && (
          <span className="text-xs text-[var(--text-ter)]">Saved. Shown to all participants.</span>
        )}
      </div>
    </div>
  );
}
